import React, { useEffect, useMemo, useState } from "react";
import useQueryParams from "../shared/hooks/useQueryParams";

const DEFAULT_RELEASE = "All";
const DEFAULT_PAGE = 1;
const MAX_VISIBLE_PAGES = 3;

function readInitialState(config) {
  const params = new URLSearchParams(window.location.search);
  const release = (params.get("release") || "").trim() || DEFAULT_RELEASE;
  const pageValue = parseInt(params.get("pageNumber") || "", 10);

  return {
    release,
    currentPage: Number.isFinite(pageValue) && pageValue > 0 ? pageValue : DEFAULT_PAGE,
    releaseOptions: Array.isArray(config.releaseOptions) && config.releaseOptions.length > 0 ? config.releaseOptions : [DEFAULT_RELEASE, "Major", "Minor"],
  };
}

function buildPageItems(currentPage, totalPages) {
  if (totalPages <= MAX_VISIBLE_PAGES) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const groupIndex = Math.floor((currentPage - 1) / MAX_VISIBLE_PAGES);
  const startPage = groupIndex * MAX_VISIBLE_PAGES + 1;
  const endPage = Math.min(startPage + MAX_VISIBLE_PAGES - 1, totalPages);

  return Array.from({ length: endPage - startPage + 1 }, (_, index) => startPage + index);
}

function scrollToMain() {
  if (window.jQuery) {
    const main = window.jQuery(".main");

    if (main.length) {
      window.jQuery("html, body").animate({ scrollTop: main.offset().top }, 300);
      return;
    }
  }

  const main = document.querySelector(".main");

  if (main) {
    main.scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

function Pagination({ currentPage, pageSize, totalItems, onPageChange, previousLabel, nextLabel }) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) {
    return null;
  }

  const visiblePages = buildPageItems(currentPage, totalPages);

  return (
    <ul role="menu" className="pagination mt-4">
      <li role="menuitem" className={`pagination-prev ${currentPage === 1 ? "disabled" : ""}`}>
        <a
          href="#"
          aria-label={previousLabel}
          aria-disabled={currentPage === 1}
          tabIndex={currentPage === 1 ? -1 : 0}
          onClick={(event) => {
            event.preventDefault();

            if (currentPage > 1) {
              onPageChange(currentPage - 1);
            }
          }}
        >
          {previousLabel}
        </a>
      </li>
      {visiblePages.map((pageNumber) => (
        <li key={pageNumber} role="menuitem" className={`pagination-page ${pageNumber === currentPage ? "active" : ""}`}>
          <a
            href="#"
            aria-current={pageNumber === currentPage ? "page" : undefined}
            onClick={(event) => {
              event.preventDefault();
              onPageChange(pageNumber);
            }}
          >
            {pageNumber}
          </a>
        </li>
      ))}
      <li role="menuitem" className={`pagination-next ${currentPage === totalPages ? "disabled" : ""}`}>
        <a
          href="#"
          aria-label={nextLabel}
          aria-disabled={currentPage === totalPages}
          tabIndex={currentPage === totalPages ? -1 : 0}
          onClick={(event) => {
            event.preventDefault();

            if (currentPage < totalPages) {
              onPageChange(currentPage + 1);
            }
          }}
        >
          {nextLabel}
        </a>
      </li>
    </ul>
  );
}

export default function ChangeReleaseFilterIsland({ config }) {
  const { setParams: setQueryParams } = useQueryParams();
  const initialState = useMemo(() => readInitialState(config), [config]);
  const [release, setRelease] = useState(initialState.release);
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [releaseOptions] = useState(initialState.releaseOptions);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let isActive = true;

    async function loadResults() {
      document.documentElement.classList.add("loading");
      setLoading(true);

      try {
        const params = new URLSearchParams();
        params.append("nodeId", String(config.nodeId || ""));
        params.append("pageNumber", String(currentPage));
        params.append("release", release !== DEFAULT_RELEASE ? release : "");

        const response = await fetch(config.filterApiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/x-www-form-urlencoded; charset=utf-8",
          },
          body: params.toString(),
        });

        if (!response.ok) {
          throw new Error(`Change release request failed with ${response.status}`);
        }

        const result = await response.json();

        if (!isActive) {
          return;
        }

        const nextResults = Array.isArray(result.Results) ? result.Results : [];
        const nextPage = Number(result.PageNumber) || DEFAULT_PAGE;

        setResults(nextResults);
        setTotal(Number(result.TotalNumberOfRecords) || 0);
        setPageSize(Number(result.PageSize) || 10);
        setCurrentPage(nextPage);

        setQueryParams(
          {
            release,
            pageNumber: nextPage,
          },
          {
            path: config.baseUrl || window.location.pathname,
            reset: true,
          },
        );
        scrollToMain();
      } catch (error) {
        if (!isActive) {
          return;
        }

        console.error(error);
        setResults([]);
        setTotal(0);
      } finally {
        if (!isActive) {
          return;
        }

        setLoading(false);
        document.documentElement.classList.remove("loading");
      }
    }

    loadResults();

    return () => {
      isActive = false;
      document.documentElement.classList.remove("loading");
    };
  }, [config.baseUrl, config.filterApiUrl, config.nodeId, currentPage, release, reloadKey, setQueryParams]);

  const onReleaseChange = (value) => {
    setRelease(value);
    setCurrentPage(DEFAULT_PAGE);
  };

  const onReset = () => {
    setRelease(DEFAULT_RELEASE);
    setCurrentPage(DEFAULT_PAGE);
    setReloadKey((value) => value + 1);
  };

  return (
    <div id="changeReleaseGrid">
      {!loading && results.length > 0 ? (
        <>
          <div className="bg-lightest-blue p-4 rounded-xl">
            <div className="filter d-flex flex-grow-1 flex-wrap flex-lg-nowrap">
              <div className="h3 mb-0 mr-3">{config.filterHeading}</div>
              <button className="btn btn-link flex-shrink-0 p-0 align-self-start ml-auto order-lg-3" type="button" onClick={onReset}>
                <u>{config.resetLabel}</u>
              </button>
              <div className="col-xl-4 col-lg-6 px-0 px-lg-3 order-lg-2">
                <div className="dropdown">
                  <button
                    className="btn-dropdown dropdown-toggle pr-0"
                    type="button"
                    id="releaseDropdown"
                    data-bs-toggle="dropdown"
                    aria-haspopup="true"
                    aria-expanded="false"
                  >
                    {release}
                  </button>
                  <div className="dropdown-menu" aria-labelledby="releaseDropdown">
                    {releaseOptions.map((item) => (
                      <button
                        key={item}
                        className={`dropdown-item ${item === release ? "active" : ""}`}
                        type="button"
                        onClick={() => onReleaseChange(item)}
                      >
                        {item}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {results.map((item, index) => (
            <table className="table mt-md-5 mt-4" key={`${item.Url || item.Title || "release"}-${index}`}>
              <tbody>
                <tr>
                  <td>
                    <div className="d-flex flex-wrap flex-md-nowrap pt-2">
                      <div className="title mr-3">
                        <div className="h3">{item.Title}</div>
                        <p className="mt-2">
                          Status:{" "}
                          <span className={`text-uppercase ${item.StatusCssClass || ""}`.trim()}>
                            <strong>{item.Status}</strong>
                          </span>
                        </p>
                      </div>
                      <a href={item.Url} className="flex-shrink-0 ml-md-auto text-nowrap btn btn-primary align-self-start mb-3">
                        {config.viewReleaseLabel}
                      </a>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <p className="mt-3">
                      <span className="text-primary mr-2">{config.deliveryTypeLabel}</span>
                      <span className={item.ReleaseTypeCssClass || ""}>{item.ReleaseType}</span>
                    </p>
                    <div className="my-3" dangerouslySetInnerHTML={{ __html: item.SummaryCopy || "" }} />
                  </td>
                </tr>
              </tbody>
            </table>
          ))}

          <Pagination
            currentPage={currentPage}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setCurrentPage}
            previousLabel={config.previousPageLabel}
            nextLabel={config.nextPageLabel}
          />
        </>
      ) : null}

      {!loading && results.length === 0 ? (
        <div className="container my-5">
          <h3 className="sorry">{config.emptyStateText}</h3>
        </div>
      ) : null}
    </div>
  );
}
